// 시공사 만들기 마법사 상태 — 컴포넌트가 아닌 값·함수는 여기에 둔다(onboarding-wizard.tsx 의 Fast Refresh 가 깨지지 않게).

import { parseOrgCreateInput } from "@buildpy/core/modules/onboarding/rules";
import {
  MONTHLY_SITES,
  type MonthlySites,
  SIGNUP_SOURCES,
  type SignupSource,
  STAFF_SIZES,
  type StaffSize,
  WORK_TYPES,
  type WorkType,
} from "@buildpy/core/modules/onboarding/types";
import type { OrgCreateForm } from "@/api/actions/onboarding";

export type WizardStep = "company" | "size" | "review";

export const ORG_CREATE_FAILED_MESSAGE = "시공사를 만들지 못했습니다. 잠시 후 다시 시도하세요.";

export interface WizardValues {
  name: string;
  slug: string;
  ceo: string;
  tel: string;
  address: string;
  bizNo: string;
  licenseNo: string;
  staffSize: StaffSize | null;
  workTypes: WorkType[];
  monthlySites: MonthlySites | null;
  source: SignupSource | null;
  terms: boolean;
  privacy: boolean;
}

export const EMPTY_WIZARD: WizardValues = {
  name: "",
  slug: "",
  ceo: "",
  tel: "",
  address: "",
  bizNo: "",
  licenseNo: "",
  staffSize: null,
  workTypes: [],
  monthlySites: null,
  source: null,
  terms: false,
  privacy: false,
};

// 마법사 값 → 액션 입력(빈 선택칸은 뺀다).
export const orgCreateFormOf = (v: WizardValues): OrgCreateForm => ({
  name: v.name.trim(),
  slug: v.slug.trim().toLowerCase(),
  tel: v.tel.trim(),
  ceo: v.ceo.trim(),
  address: v.address.trim() || undefined,
  bizNo: v.bizNo.trim() || undefined,
  licenseNo: v.licenseNo.trim() || undefined,
  staffSize: v.staffSize,
  workTypes: v.workTypes,
  monthlySites: v.monthlySites,
  source: v.source,
  agree: { terms: v.terms, privacy: v.privacy },
});

// 회사 정보 단계 검사 — 동의는 확인 단계에서 받으므로 여기선 동의한 셈 치고 core 판정 한 벌을 그대로 쓴다.
export const companyErrorOf = (v: WizardValues): string | null => {
  const parsed = parseOrgCreateInput({ ...orgCreateFormOf(v), agree: { terms: true, privacy: true } });
  return parsed.ok ? null : parsed.error;
};

// 확인 단계 요약 — 만들기가 저장할 정리값(core 판정 한 벌) · 회사 정보 단계를 지나야 오므로 실패 분기는 액션 입력 그대로.
export const wizardReviewOf = (v: WizardValues): { name: string; slug: string; ceo: string; tel: string } => {
  const form = orgCreateFormOf(v);
  const parsed = parseOrgCreateInput({ ...form, agree: { terms: true, privacy: true } });
  const { name, slug, ceo, tel } = parsed.ok ? parsed.value : form;
  return { name, slug, ceo, tel };
};

export interface WizardDraft {
  step: WizardStep;
  values: WizardValues;
}

const pick = <T extends string>(options: readonly T[], value: unknown): T | null =>
  typeof value === "string" && (options as readonly string[]).includes(value) ? (value as T) : null;

// 탭에 적어 둔 초안 읽기 — 깨졌거나 모르는 값은 버리고, 회사 정보가 안 맞으면 1단계부터 · 동의는 초안에서 이어 오지 않는다(매번 직접 체크).
export const wizardDraftOf = (raw: string | null): WizardDraft | null => {
  if (!raw) return null;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!data || typeof data !== "object") return null;
  const { step, values } = data as { step?: unknown; values?: unknown };
  if (!values || typeof values !== "object") return null;
  const r = values as Record<string, unknown>;
  const str = (key: keyof WizardValues) => (typeof r[key] === "string" ? (r[key] as string) : "");
  const parsed: WizardValues = {
    name: str("name"),
    slug: str("slug"),
    ceo: str("ceo"),
    tel: str("tel"),
    address: str("address"),
    bizNo: str("bizNo"),
    licenseNo: str("licenseNo"),
    staffSize: pick(STAFF_SIZES, r.staffSize),
    workTypes: Array.isArray(r.workTypes) ? WORK_TYPES.filter((t) => (r.workTypes as unknown[]).includes(t)) : [],
    monthlySites: pick(MONTHLY_SITES, r.monthlySites),
    source: pick(SIGNUP_SOURCES, r.source),
    terms: false,
    privacy: false,
  };
  const at = pick(["company", "size", "review"] as const, step) ?? "company";
  return { step: at !== "company" && companyErrorOf(parsed) ? "company" : at, values: parsed };
};

// 초안에 적을 값 — 약관 동의·처리방침 확인은 빼고 적는다.
export const draftTextOf = (draft: WizardDraft): string => {
  const { terms: _terms, privacy: _privacy, ...rest } = draft.values;
  return JSON.stringify({ step: draft.step, values: rest });
};
